import React from 'react';

function scoreStatusLabel(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return '';
  if (n >= 85) return 'Great';
  if (n >= 70) return 'Good';
  if (n >= 55) return 'Fair';
  if (n >= 40) return 'Poor';
  return 'Critical';
}

/** Circular ring score matching the reference report (not the old needle gauge). */
function ScoreRing({ score, status, onInfo }) {
  const size = 152;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const offset = c * (1 - pct / 100);

  return (
    <div className="skin-report-ring" role="img" aria-label={`Overall skin score ${score} out of 100`}>
      <svg className="skin-report-ring-svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          className="skin-report-ring-track"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
        />
        <circle
          className="skin-report-ring-progress"
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="skin-report-ring-center">
        <div className="skin-report-ring-value">
          {score}
          <span className="skin-report-ring-denom">/100</span>
        </div>
        {status ? (
          <div className={`skin-report-ring-status skin-report-ring-status--${status.toLowerCase()}`}>
            {status}
          </div>
        ) : null}
        <div className="skin-report-ring-label">
          Skin Score
          {onInfo ? (
            <button type="button" className="skin-report-info-btn" aria-label="About skin score" onClick={onInfo}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Reference top: face + quote | circular score, then what-this-means below score, then scale + chips.
 * Scan / ID / Gender live only in the page masthead chips (not duplicated here).
 */
export default function SkinReportHero({
  faceImage,
  concernLabel = null,
  concernPreviewCanvasRef,
  overallSkinScore,
  skinAgeValue,
  skinAgeLoading,
  displaySkinType,
  displaySkinColor,
  summaryText,
  summaryLoading,
  showAngleNav,
  concernView,
  onPrevAngle,
  onNextAngle,
  onSkinAgeInfo,
  onSkinScoreInfo,
  pdfMode = false,
}) {
  const status = scoreStatusLabel(overallSkinScore);
  const scorePct = overallSkinScore != null
    ? Math.max(0, Math.min(100, Number(overallSkinScore)))
    : null;
  const markerLeft = scorePct != null ? `${scorePct}%` : '0%';
  const showNav = showAngleNav && !pdfMode;
  const showConcern = Boolean(concernLabel) && !pdfMode;

  return (
    <section className={`skin-report-hero${pdfMode ? ' skin-report-hero--pdf' : ''}`} id="section-user-info">
      <div className="skin-report-hero-grid">
        <div className="skin-report-hero-left">
          <div
            className={`skin-report-face-frame${showConcern ? ' skin-report-face-frame--concern-preview' : ''}`}
          >
            {showNav ? (
              <div className="skin-report-face-nav-bar" data-pdf-exclude="true">
                <button type="button" className="skin-report-face-nav" onClick={onPrevAngle} aria-label="Previous angle">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                </button>
                <span className="skin-report-face-angle">{String(concernView || 'front').replace(/^\w/, (c) => c.toUpperCase())}</span>
                <button type="button" className="skin-report-face-nav" onClick={onNextAngle} aria-label="Next angle">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              </div>
            ) : null}
            {faceImage ? (
              <img src={faceImage} alt="Skin analysis face" className="skin-report-face-image" />
            ) : (
              <div className="skin-report-face-empty">No face image</div>
            )}
            <canvas
              ref={concernPreviewCanvasRef}
              className="skin-report-face-canvas"
              aria-hidden="true"
              data-pdf-exclude="true"
            />
            {showConcern ? (
              <span className="skin-report-face-concern-label">{concernLabel}</span>
            ) : null}
          </div>

          <blockquote className="skin-report-quote">
            Your skin is unique. Let&apos;s decode it with intelligence.
          </blockquote>
        </div>

        <div className="skin-report-score-cluster">
          <h2 className="skin-report-score-heading">Your Overall Skin Score</h2>

          <div className="skin-report-score-stack">
            {overallSkinScore != null ? (
              <ScoreRing score={overallSkinScore} status={status} onInfo={onSkinScoreInfo} />
            ) : (
              <div className="skin-report-loader" aria-busy="true">Loading score…</div>
            )}

            <div className="skin-report-what-means" id="section-what-this-means">
              <h3 className="skin-report-section-label">What does this mean?</h3>
              {summaryLoading ? (
                <div className="skin-report-loader" aria-busy="true">
                  <div className="loading-spinner" />
                  <p>Generating summary…</p>
                </div>
              ) : (
                <p className="skin-report-what-means-text">
                  {summaryText || 'Your personalized skin summary will appear here.'}
                </p>
              )}
            </div>
          </div>

          {scorePct != null ? (
            <div
              className="skin-report-score-scale"
              role="img"
              aria-label={`Score marker at ${Math.round(scorePct)} of 100`}
            >
              <div className="skin-report-score-scale-track">
                <span
                  className="skin-report-score-scale-marker"
                  style={{ left: markerLeft }}
                />
              </div>
              <div className="skin-report-score-scale-labels">
                <span>0</span>
                <span>50</span>
                <span>100</span>
              </div>
            </div>
          ) : null}

          <div className="skin-report-stat-pills">
            <div className="skin-report-stat-pill">
              <span className="skin-report-stat-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 19V5M4 19h16"/><path d="M8 15l3-4 3 2 5-7"/></svg>
              </span>
              <span className="skin-report-stat-label">Skin Age</span>
              {skinAgeLoading ? (
                <span className="skin-report-loader skin-report-loader--inline">Estimating…</span>
              ) : (
                <span className="skin-report-stat-value">
                  {skinAgeValue != null ? `${skinAgeValue} yrs` : '—'}
                  {skinAgeValue != null && onSkinAgeInfo ? (
                    <button type="button" className="skin-report-info-btn" aria-label="Why this skin age" onClick={onSkinAgeInfo}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    </button>
                  ) : null}
                </span>
              )}
            </div>
            <div className="skin-report-stat-pill">
              <span className="skin-report-stat-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3s5 5.2 5 9a5 5 0 0 1-10 0c0-3.8 5-9 5-9z"/></svg>
              </span>
              <span className="skin-report-stat-label">Skin Type</span>
              <span className="skin-report-stat-value">{displaySkinType || '—'}</span>
            </div>
            <div className="skin-report-stat-pill">
              <span className="skin-report-stat-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="8"/><path d="M12 4a8 8 0 0 1 0 16"/></svg>
              </span>
              <span className="skin-report-stat-label">Skin Color</span>
              <span className="skin-report-stat-value">
                {displaySkinColor || (skinAgeLoading ? <span className="skin-report-loader skin-report-loader--inline">…</span> : '—')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
